class IngredientsGrid extends HTMLElement {
    constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleTabChange = this.handleTabChange.bind(this);
        this.modal = document.querySelector('[data-modal="ingredient-modal"]');
        this.cards = [];
        this.filterTimeline = null;
        this.tabComponent = this.querySelector('ui-tabs');
        this.selectedFlavor =
            this.modal?.querySelector('[data-current-flavor]')?.textContent?.trim() || '';
    }

    connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('article'));
        this.addEventListener('click', this.handleClick);
        this.modal?.addEventListener('click', this.handleClick);
        this.tabComponent?.addEventListener('ui:tabchange', this.handleTabChange);

        const initialValue = this.tabComponent
            ?.querySelector('[role="tab"][aria-selected="true"]')
            ?.dataset?.value;

        if (initialValue) {
            this.selectedFlavor = initialValue.trim();
            this.filterByFlavor(initialValue, true);
            this.syncModalFlavorState(this.modal);
        }
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.modal?.removeEventListener('click', this.handleClick);
        this.tabComponent?.removeEventListener('ui:tabchange', this.handleTabChange);
        this.filterTimeline?.kill();
    }

    handleTabChange(event) {
        const value = event.detail?.value;
        if (!value) return;

        this.selectedFlavor = value.trim();
        const modalSubtitle = this.modal?.querySelector('[data-modal-subtitle]');
        if (modalSubtitle) modalSubtitle.textContent = value;
        this.syncModalFlavorState(this.modal);
        this.filterByFlavor(value);
    }

    syncModalFlavorState(root) {
        if (!root || !this.selectedFlavor) return;

        const selected = this.selectedFlavor.toLowerCase();
        const currentFlavor = root.querySelector('[data-current-flavor]');
        if (currentFlavor) currentFlavor.textContent = this.selectedFlavor;

        const flavoredItems = root.querySelectorAll('[data-unique-parent-flavor]');
        flavoredItems.forEach(item => {
            const rawFlavors = item.getAttribute('data-unique-parent-flavor') || '';
            const flavors = rawFlavors
                .split(',')
                .map(flavor => flavor.trim().toLowerCase())
                .filter(Boolean);

            const shouldShow = !flavors.length || flavors.includes(selected);
            item.classList.toggle('hidden', !shouldShow);
        });
    }

    filterByFlavor(value, instant = false) {
        const selected = value.trim().toLowerCase();
        if (!selected || !this.cards.length) return;

        const toShow = [];
        const toHide = [];

        this.cards.forEach(card => {
            const rawFlavors =
                card.dataset.uniqueParentFlavor ||
                card.dataset.parentFlavor ||
                '';
            const flavors = rawFlavors
                .split(',')
                .map(flavor => flavor.trim().toLowerCase())
                .filter(Boolean);

            // No flavor tag means this card should be available under every tab.
            const shouldShow = !flavors.length || flavors.includes(selected);
            if (shouldShow) toShow.push(card);
            else toHide.push(card);
        });

        if (instant) {
            toHide.forEach(card => {
                gsap.killTweensOf(card);
                card.style.display = 'none';
                gsap.set(card, { autoAlpha: 0, y: 8 });
            });
            toShow.forEach(card => {
                gsap.killTweensOf(card);
                card.style.display = '';
                gsap.set(card, { autoAlpha: 1, y: 0 });
            });
            return;
        }

        this.filterTimeline?.kill();

        const currentlyVisible = this.cards.filter(card => card.style.display !== 'none');
        currentlyVisible.forEach(card => gsap.killTweensOf(card));
        toShow.forEach(card => gsap.killTweensOf(card));

        this.filterTimeline = gsap.timeline({
            defaults: { ease: 'smooth' },
        });

        if (currentlyVisible.length) {
            this.filterTimeline.to(currentlyVisible, {
                autoAlpha: 0,
                y: -8,
                duration: 0.3,
                stagger: 0.015,
            });
        }

        this.filterTimeline.add(() => {
            this.cards.forEach(card => {
                card.style.display = 'none';
                gsap.set(card, { autoAlpha: 0, y: 8 });
            });
            toShow.forEach(card => {
                card.style.display = '';
            });
        });

        if (toShow.length) {
            this.filterTimeline.to(toShow, {
                autoAlpha: 1,
                y: 0,
                duration: 0.3,
                stagger: 0.02,
            });
        }
    }

    async handleClick(event) {
        const button = event.target.closest('button[data-url]');
        if (!button) return;

        const clickedInsideGrid = this.contains(button);
        const clickedInsideModal = this.modal?.contains(button);
        if (!clickedInsideGrid && !clickedInsideModal) return;

        event.preventDefault();

        const url = button.getAttribute('data-url');
        if (!url) return;

        document.dispatchEvent(new Event('loading:start'));

        try {
            const response = await fetch(url, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const html = await response.text();

            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const newModalContent = doc.querySelector('ing-modal');
            const currentModalContent = this.modal.querySelector('ing-modal');

            if (newModalContent && currentModalContent) {
                this.syncModalFlavorState(newModalContent);
                currentModalContent.outerHTML = newModalContent.outerHTML;
            }

            const modalScrollContainer = this.modal?.querySelector('[data-scroll-container]');
            if (modalScrollContainer) {
                modalScrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
            }

            this.modal.open();
            document.dispatchEvent(new Event('loading:end'));
        } catch (error) {
            console.error('Error fetching ingredient modal:', error);
        }
    }
}

customElements.define('ingredients-grid', IngredientsGrid);


class IngSwiper extends HTMLElement {
    constructor() {
        super()
        this.swiper = null
    }

    connectedCallback() {
        const container = this.querySelector('.swiper')
        const prevButton = this.querySelector('[data-swiper-button="left"]')
        const nextButton = this.querySelector('[data-swiper-button="right"]')

        this.swiper = new Swiper(container, {
            slidesPerView: 'auto',
            spaceBetween: 16,
            speed: 500,
            watchOverflow: true,
            navigation: {
                prevEl: prevButton,
                nextEl: nextButton,
                disabledClass: 'opacity-30 cursor-not-allowed',
            },
        })
    }

    disconnectedCallback() {
        if (this.swiper) {
            this.swiper.destroy(true, true)
        }
    }
}

customElements.define('ing-swiper', IngSwiper)